import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

class ProductInventoryNew extends HTMLElement {

  constructor () {

    super ()

    //Internationalized string inherited from liquid`
    this.I18N = {
      'inventory_in_stock_show_count': this.getAttribute ("I18N-inventory-in-stock-show-count"),
      'inventory_in_stock': this.getAttribute ("I18N-inventory-in-stock"),
      'inventory_low_stock_show_count': this.getAttribute ("I18N-inventory-low-stock-show-count"),
      'inventory_low_stock': this.getAttribute ("I18N-inventory-low-stock"),
      'inventory_out_of_stock_continue_selling': this.getAttribute ("I18N-inventory-out-of-stock-continue-selling"),
      'inventory_out_of_stock': this.getAttribute ("I18N-inventory-out-of-stock"),
      'inventory_fetch_failed': this.getAttribute ("I18N-inventory-fetch-failed")
    }

    //Liquid block settings
    this.blockSettings = {
      "inventory_threshold":  this.getAttribute ("data-inventory-threshold"),
      "show_inventory_quantity": this.getAttribute ("data-show-inventory-quantity"),
      "inventory_location_name": this.getAttribute ("data-location-name"),
      "inventory_location_id": this.getAttribute ("data-location-id"),
      "inventory_block_id": this.getAttribute ("data-block-id"),
      "inventory_type": this.getAttribute ("data-inventory-type")
    }

    //Liquid variant attributes
    this.variantAttributes = {
      "inventory_policy": this.getAttribute ("data-inventory-policy"),
      "inventory_management": this.getAttribute ("data-inventory-management")
    }
    //Fetch inventory and initialize the html
    this.fetchInventories ()

  }

  connectedCallback() {

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);

  }

  disconnectedCallback() {

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);

  }

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {

    if (event.detail.data.newProduct) {

      this.dataset.productId = event.detail.data.newProduct.id;

    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {

      return;

    }

    const newInventory = event.detail.data.html.querySelector('product-inventory-new');

    if (!newInventory) return;

    morph(this, newInventory, { childrenOnly: true });

    this.dataset.variantId = event.detail.resource?.id || null

    if (this.dataset.variantId === "null") {
      this.renderInventory("Unavailable")
    } else {
      this.fetchInventories ()
    }

  };

  async fetchInventories () {

    //Try to fetch both total and local inventory via the Shopify Storefront API. 
    //If fetch fails render a client side error and log an error to the console
    try {

      let totalInventory = await this.fetchTotalInventory ()
      let localInventoryEdges = await this.fetchLocalInventory ()

      let renderableInventory = this.calculateInventory (totalInventory, localInventoryEdges)

      this.renderInventory (renderableInventory)

    } catch (err) {

      this.renderInventory ("Failed")
      console.error ("Failed to fetch varinat inventory", err)

    }

  }

  //Fetches total inventory for the currently selected variant from the Shopify Storefront API
  async fetchTotalInventory () {

    const query = `
        {
            node(id: "gid://shopify/ProductVariant/${this.dataset.variantId}") {
                ... on ProductVariant {
                    quantityAvailable
                }
            }
        }
    `

    const response = await fetch("https://buyn.se/api/2025-07/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": "877da55bd1bfbfbb80858baf19e8b275"
      },
      body: JSON.stringify({ query })
    });

    const result = await response.json();
    return result.data.node.quantityAvailable

  }

  //Fetches local inventories from physical locations returned by the Shopify Storefront API
  //NOTE: Only fetches the first 5 locations which works for todays needs.
  //TODO: This needs to be generalized to an arbitrary amount of locations, probably through some sort of pagination.
  async fetchLocalInventory () {

    const query = `
      query {
          node (id: "gid://shopify/ProductVariant/${this.dataset.variantId}") {
              ... on ProductVariant {
                  storeAvailability(first: 5) {
                      edges {
                          node {
                              available
                              quantityAvailable
                              location {
                                  name
                                  id
                              }
                          }
                      }
                  }
              }
          }
      }
    `

    const response = await fetch("https://buyn.se/api/2025-07/graphql.json", {

      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": "877da55bd1bfbfbb80858baf19e8b275"
      },
      body: JSON.stringify({ query })

    });

    const result = await response.json();
    return result.data.node.storeAvailability.edges;

  }

  //Takes the variants total inventory from all locations and the inventory from all physical locations 
  //and calculates the relevant inventory based on the type the inventory block is set to.
  calculateInventory (totalInventory, localInventoryEdges) {

      let inventoryQuantity = 0

      switch (this.blockSettings.inventory_type) {

        case "all":
          inventoryQuantity = totalInventory
          break;

        case "distributor":
          inventoryQuantity = totalInventory - localInventoryEdges.map (e => e.node).reduce((sum, a) => sum + (a.quantityAvailable || 0), 0)
          break;

        case "local":
          inventoryQuantity = localInventoryEdges.map(e => e.node).find(a => a.location.id === `gid://shopify/Location/${this.blockSettings.inventory_location_id}`)?.quantityAvailable;
          break;

      }
      return inventoryQuantity

  }

  //Does what it says it does.
  renderInventory (inventoryQuantity) {

    //Get the child elements to be modified
    let inventoryHTMLParent = this.querySelector('.product-inventory__text')
    let inventoryStatusIconHTML = this.querySelector('.svg-wrapper')

    //If inventory fetch returned an error, display "Failed to fetch" to the user
    if (inventoryQuantity === "Failed") {

      inventoryHTMLParent.innerHTML = `${this.I18N.inventory_fetch_failed}`
      inventoryStatusIconHTML.className = 'svg-wrapper product-inventory__icon product-inventory__icon-out_of_stock'

    } else if (inventoryQuantity === "Unavailable") {

      inventoryHTMLParent.innerHTML = `Ej tillgänglig`
      inventoryStatusIconHTML.className = 'svg-wrapper product-inventory__icon product-inventory__icon-out_of_stock'

    } else {

      if (this.variantAttributes.inventory_management == "shopify") {

        //If product is in stock
        if (inventoryQuantity > 0) {

          //If product stock is below the threshold for being considered low
          if (inventoryQuantity <= this.blockSettings.inventory_threshold) {

            inventoryStatusIconHTML.className = 'svg-wrapper product-inventory__icon product-inventory__icon-low'

            //Display quantity in stock
            if (this.blockSettings.show_inventory_quantity) {

              inventoryHTMLParent.innerHTML = `${this.I18N.inventory_low_stock_show_count.replace ("replaceThisString", inventoryQuantity)}`
            
            //Don't display quantity in stock
            } else {

              inventoryHTMLParent.innerHTML =`${this.I18N.inventory_low_stock}`

            }

          //If product stock is above the threshold for being considered low
          } else {

            inventoryStatusIconHTML.className = 'svg-wrapper product-inventory__icon product-inventory__icon-in_stock'

            //Display quantity in stock
            if (this.blockSettings.show_inventory_quantity) {

              inventoryHTMLParent.innerHTML = `${this.I18N.inventory_in_stock_show_count.replace ("replaceThisString", inventoryQuantity)}`

            //Don't display quantity in stock
            } else {

              inventoryHTMLParent.innerHTML = `${this.I18N.inventory_in_stock}`

            }

          }

        //If product is out of stock
        } else {

          inventoryStatusIconHTML.className = 'svg-wrapper product-inventory__icon product-inventory__icon-out_of_stock'

          //If inventory policy for the variant is to continue selling when out of stock
          if (this.variantAttributes.inventory_policy == "continue") {

            inventoryHTMLParent.innerHTML = `${this.I18N.inventory_out_of_stock_continue_selling}`

          //If inventory policy for the variant is to not continue selling when out of stock
          } else {

            inventoryHTMLParent.innerHTML = `${this.I18N.inventory_out_of_stock}`

          }

        }

      }

    }

  }

}

if (!customElements.get('product-inventory-new')) {

  customElements.define('product-inventory-new', ProductInventoryNew);

}